import Button from 'components/Button';
import FloatingButton from 'components/FloatingButton';
import TagsFilter from 'components/Forms/lib/TagsFilter';
import MultiActionsButton from 'components/MultiActionsButton';
import SelectFilters from 'components/SelectFilters';
import { Civility } from 'core/enums/civility';
import ContactStatus from 'core/enums/contactStatus';
import EventType from 'core/enums/eventType';
import Contact from 'core/models/contact';
import Tag from 'core/models/tag';
import { useContactIdsWithApplication } from 'core/api/applications';
import { useContactsQuery } from 'core/api/contacts';
import { getContactLabel, getContactLabelWithoutCivility, getParentOfContact } from 'core/useCases/contacts/utils';
import { DisabledField, ModalType } from 'core/useCases/modal/types';
import { useModalStore } from 'stores/modal';
import useResponsive from 'hooks/responsive';
import { useIsPremium } from 'hooks/userIsPremium';
import Add from 'images/icons/Add.svg?react';
import Edit from 'images/icons/Edit.svg?react';
import PremiumIcon from 'components/PremiumIcon';
import useScrollMargin from 'hooks/scrollMargin';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { SBoldPrimaryText, STable } from 'styles/components';
import { SCenteredColumn, SEndRow } from 'styles/layout';
import normalizeText from 'utils/normalizeText';
import ContactStatusComponent from '../ContactStatus';
import DeleteContactButton from '../DeleteContactButton';
import ExportContact from './ExportContact';
import {
    SActions,
    SImportButton,
    SCard,
    SCardListInner,
    SScrollContainer,
    SVirtualCard,
    SCardHeader,
    SCardHeaderContact,
    SCardHeaderLeft,
    SCardLine,
    SCardLineLeft,
    SCardLineRight,
    SColumnResponsive,
    SFemale,
    SMale,
    SPremiumButtonDiv,
    SPremiumDiv,
    SRight,
    SRightResponsive,
    SSearchInput,
    SSpacedRowResponsive,
    STitle,
    SWrapper,
} from './styles';

const ContactList: FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { isMobile } = useResponsive();
    // Mémoïsé : `data ?? []` créerait un nouveau tableau à chaque render pendant le
    // chargement, ce qui relancerait en boucle le useEffect de filtrage (setState).
    const { data: contactsData } = useContactsQuery();
    const contacts = useMemo(() => contactsData ?? [], [contactsData]);
    const contactIdsWithApplication = useContactIdsWithApplication().data ?? [];
    const openModal = useModalStore((s) => s.openModal);
    const [search, setSearch] = useState<string>('');
    const [filteredContact, setFilteredContact] = useState<Contact[]>([]);
    const [filterOptionsValues, setFilterOptionsValues] = useState<string[]>([]);
    const history = useHistory();
    const isPremium = useIsPremium();
    const defaultTag: Tag = history.location.state?.tag;

    const [tagsFilters, setTagsFilters] = useState<Tag[]>(defaultTag ? [defaultTag] : []);

    // Scroll géré par la fenêtre : la barre de scroll reste sur les bords du
    // navigateur. scrollMargin = offset de la liste dans le document, requis par
    // useWindowVirtualizer pour positionner correctement les éléments.
    const { ref: scrollRef, scrollMargin } = useScrollMargin<HTMLDivElement>();

    const rowVirtualizer = useWindowVirtualizer({
        count: filteredContact.length,
        estimateSize: () => 60,
        overscan: 10,
        scrollMargin,
    });

    const cardVirtualizer = useWindowVirtualizer({
        count: filteredContact.length,
        estimateSize: () => 240,
        overscan: 10,
        scrollMargin,
    });

    const filterOptions = [
        {
            component: <ContactStatusComponent status={ContactStatus.Engaged} />,
            value: ContactStatus.Engaged.toString(),
        },
        {
            component: <ContactStatusComponent status={ContactStatus.Incomplete} />,
            value: ContactStatus.Incomplete.toString(),
        },
        {
            component: <ContactStatusComponent status={ContactStatus.New} />,
            value: ContactStatus.New.toString(),
        },
        {
            component: <ContactStatusComponent status={ContactStatus.Thanked} />,
            value: ContactStatus.Thanked.toString(),
        },
    ];

    const sortedContacts = [...contacts].sort((a: Contact, b: Contact): number => {
        const label1 = `${a.lastName} ${a.firstName}`;
        const label2 = `${b.lastName} ${b.firstName}`;
        if (label1.toLowerCase() > label2.toLowerCase()) {
            return 1;
        }
        return -1;
    });

    const openContactModal = () => {
        openModal({
            title: t('contact.add-contact'),
            type: ModalType.Contact,
        });
    };

    const openContactsModal = () => {
        openModal({
            title: t('contact.import-contacts'),
            type: ModalType.Contacts,
        });
    };

    const openPromptGeneratorModal = () => {
        openModal({
            title: t('prompt-generator.modal-title'),
            type: ModalType.PromptGenerator,
        });
    };

    const openEditContactModal = (contact: Contact) => {
        openModal({
            defaultValues: contact,
            title: t('contact-details.update-contact'),
            type: ModalType.Contact,
        });
    };

    const openTaskModal = (contact: Contact) => {
        openModal({
            defaultValues: {
                contact,
            },
            disabledFields: [DisabledField.ContactId],
            title: t('task-list.add-task'),
            type: ModalType.Task,
        });
    };

    const openInterviewModal = (contact: Contact) => {
        openModal({
            defaultValues: {
                contact,
                type: EventType.Interview,
            },
            disabledFields: [DisabledField.ContactId],
            title: t('activities.add-interview'),
            type: ModalType.Event,
        });
    };

    const openApplicationModal = (contact: Contact) => {
        openModal({
            defaultValues: {
                contacts: [contact],
            },
            title: t('application.add-application'),
            type: ModalType.Application,
        });
    };

    useEffect(() => {
        let localContacts = [...sortedContacts];

        if (filterOptionsValues.length > 0) {
            localContacts = localContacts.filter((contact) => filterOptionsValues.includes(contact.status));
        }

        if (tagsFilters.length > 0) {
            localContacts = localContacts.filter((contact) =>
                tagsFilters.some((tag) => contact.tags.some((contactTag: Tag) => contactTag.name === tag.name)),
            );
        }

        if (search) {
            const computedSearch = normalizeText(search);
            setFilteredContact(
                localContacts.filter(
                    (contact) =>
                        normalizeText(getContactLabelWithoutCivility(contact)).includes(computedSearch) ||
                        (contact.company && normalizeText(contact.company).includes(computedSearch)),
                ),
            );
        } else {
            setFilteredContact(localContacts);
        }
    }, [contacts, search, filterOptionsValues, tagsFilters]);

    const handleSearchClick = () => {
        if (!isPremium) {
            history.push('/ask-subscription');
        }
    };

    const SearchInput = (
        <SSearchInput>
            <input
                onChange={(event) => setSearch(event.target.value)}
                onClick={handleSearchClick}
                placeholder={t('contact.search-for-a-contact')}
                type="text"
                value={search}
            />
            <SPremiumDiv>
                <PremiumIcon />
            </SPremiumDiv>
        </SSearchInput>
    );

    const redirectToAskSubscription = () => {
        if (isPremium === false) {
            history.push('/ask-subscription');
        }
    };

    const ImportButton = (
        <SImportButton onClick={!isPremium ? redirectToAskSubscription : openContactsModal} withShadow>
            <SPremiumButtonDiv>
                <PremiumIcon />
            </SPremiumButtonDiv>
            {t('contact.import-contacts')}
        </SImportButton>
    );

    const AiImportButton = (
        <SImportButton onClick={!isPremium ? redirectToAskSubscription : openPromptGeneratorModal} withShadow>
            <SPremiumButtonDiv>
                <PremiumIcon />
            </SPremiumButtonDiv>
            {t('prompt-generator.button')}
        </SImportButton>
    );

    const Actions = () => (
        <>
            {AiImportButton}
            {ImportButton}
            <Button onClick={openContactModal} withShadow>
                {t('contact.add-contact')}
            </Button>
        </>
    );

    return (
        <SWrapper>
            <SSpacedRowResponsive>
                <SColumnResponsive>
                    <STitle>{t('page.contact.title')}</STitle>
                    <span>
                        {contacts.length} {t('common.contacts')}
                    </span>
                </SColumnResponsive>
                <SRightResponsive>
                    <TagsFilter
                        isPremium={isPremium}
                        onChange={setTagsFilters}
                        onClickToPremiumField={redirectToAskSubscription}
                        tagFilters={tagsFilters}
                        isPremiumFeature
                    />
                    {SearchInput}
                    <SRight>
                        <SelectFilters
                            isPremium={isPremium}
                            options={filterOptions}
                            selectedOptionsValues={filterOptionsValues}
                            setSelectedOptionsValues={setFilterOptionsValues}
                            isPremiumFeature
                        />
                        <ExportContact />
                    </SRight>
                </SRightResponsive>
            </SSpacedRowResponsive>
            {isMobile ? (
                <SScrollContainer ref={scrollRef}>
                    <SCardListInner style={{ height: `${cardVirtualizer.getTotalSize()}px` }}>
                        {cardVirtualizer.getVirtualItems().map((virtualItem) => {
                            const index = virtualItem.index;
                            const contact = filteredContact[index];
                            const { id, company, mobileNumber, phoneNumber, email, rank, status, parent, civility } =
                                contact;
                            const parentLabel = getParentOfContact(contact, t);
                            const contactLabel = getContactLabel(contact, t);

                            return (
                                <SVirtualCard
                                    key={id}
                                    ref={cardVirtualizer.measureElement}
                                    data-index={index}
                                    style={{ transform: `translateY(${virtualItem.start - scrollMargin}px)` }}
                                >
                                    <SCard isEven={index % 2 === 0}>
                                        <SCardHeader>
                                <SCardHeaderLeft>
                                    {Civility.Male === civility ? <SMale /> : <SFemale />}
                                    <SCardHeaderContact>
                                        <Link title={contactLabel} to={`/contact/${id}`}>
                                            <SBoldPrimaryText>{contactLabel}</SBoldPrimaryText>
                                        </Link>
                                        <ContactStatusComponent status={status} />
                                    </SCardHeaderContact>
                                </SCardHeaderLeft>
                                <SActions className="showOnRowHover">
                                    <MultiActionsButton parentButtonContent={<Add />} iconButton>
                                        <Button onClick={() => openTaskModal(contact)} variant="popinText">
                                            {t('contact-details.popin-button-task')}
                                        </Button>
                                        <Button onClick={() => openInterviewModal(contact)} variant="popinText">
                                            {t('contact-details.popin-button-interview')}
                                        </Button>
                                        <Button onClick={() => openApplicationModal(contact)} variant="popinText">
                                            {t('contact-details.popin-button-application')}
                                        </Button>
                                    </MultiActionsButton>
                                    <button
                                        onClick={() => openEditContactModal(contact)}
                                        title={t('common.edit')}
                                        type="button"
                                    >
                                        <Edit />
                                    </button>
                                    <DeleteContactButton contact={contact} />
                                </SActions>
                            </SCardHeader>
                            <SCardLine>
                                <SCardLineLeft>{t('common.company.label')}</SCardLineLeft>
                                <SCardLineRight>{company}</SCardLineRight>
                            </SCardLine>
                            <SCardLine>
                                <SCardLineLeft>{t('common.phone')}</SCardLineLeft>
                                <SCardLineRight>
                                    {mobileNumber} <br />
                                    {phoneNumber}
                                </SCardLineRight>
                            </SCardLine>
                            <SCardLine>
                                <SCardLineLeft>{t('common.email.label')}</SCardLineLeft>
                                <SCardLineRight>{email}</SCardLineRight>
                            </SCardLine>
                            <SCardLine>
                                <SCardLineLeft>{t('common.rank')}</SCardLineLeft>
                                <SCardLineRight>
                                    <SBoldPrimaryText>{rank}</SBoldPrimaryText>
                                    {' - '}
                                    { }
                                    {parent ? (
                                        <Link title={parentLabel} to={`/contact/${parent.id}`}>
                                            {parentLabel}
                                        </Link>
                                    ) : (
                                        <span>{parentLabel}</span>
                                    )}
                                </SCardLineRight>
                            </SCardLine>
                            <SCardLine>
                                <SCardLineLeft>{t('application.label')}</SCardLineLeft>
                                <SCardLineRight>
                                    {contactIdsWithApplication.includes(id) && <SCenteredColumn>X</SCenteredColumn>}
                                </SCardLineRight>
                            </SCardLine>
                                    </SCard>
                                </SVirtualCard>
                            );
                        })}
                    </SCardListInner>
                </SScrollContainer>
            ) : (
                <SScrollContainer ref={scrollRef}>
                    <STable>
                        <thead>
                            <tr>
                                <th>{t('common.last-name.label')}</th>
                                <th>{t('application.label')}</th>
                                <th>{t('common.company.label')}</th>
                                <th>{t('common.phone')}</th>
                                <th>{t('common.email.label')}</th>
                                <th>{t('common.rank')}</th>
                                <th>{t('common.status')}</th>
                                <th aria-label={t('common.action')} />
                            </tr>
                        </thead>
                        <tbody className="table--contact">
                            {(() => {
                                const virtualRows = rowVirtualizer.getVirtualItems();
                                const totalSize = rowVirtualizer.getTotalSize();
                                const paddingTop = virtualRows.length > 0 ? virtualRows[0].start - scrollMargin : 0;
                                const paddingBottom =
                                    virtualRows.length > 0
                                        ? totalSize - (virtualRows[virtualRows.length - 1].end - scrollMargin)
                                        : 0;

                                return (
                                    <>
                                        {paddingTop > 0 && (
                                            <tr>
                                                <td colSpan={8} style={{ height: `${paddingTop}px`, padding: 0 }} />
                                            </tr>
                                        )}
                                        {virtualRows.map((virtualRow) => {
                                            const contact = filteredContact[virtualRow.index];
                                            const { id, company, mobileNumber, phoneNumber, email, rank, status, parent } =
                                                contact;
                                            const parentLabel = getParentOfContact(contact, t);
                                            const contactLabel = getContactLabel(contact, t);
                                            const rowBackground =
                                                virtualRow.index % 2 === 0
                                                    ? theme.color.grey.lighter
                                                    : theme.color.white;

                                            return (
                                                <tr key={id} ref={rowVirtualizer.measureElement} data-index={virtualRow.index}>
                                    <td style={{ background: rowBackground }}>
                                        <Link title={contactLabel} to={`/contact/${id}`}>
                                            <SBoldPrimaryText>{contactLabel}</SBoldPrimaryText>
                                        </Link>
                                    </td>
                                    <td style={{ background: rowBackground }}>
                                        {contactIdsWithApplication.includes(id) && <SCenteredColumn>X</SCenteredColumn>}
                                    </td>
                                    <td style={{ background: rowBackground }}>{company}</td>
                                    <td style={{ background: rowBackground }}>
                                        {mobileNumber} <br />
                                        {phoneNumber}
                                    </td>
                                    <td style={{ background: rowBackground }}>{email}</td>
                                    <td style={{ background: rowBackground }}>
                                        <SBoldPrimaryText>{rank}</SBoldPrimaryText>
                                        <br />
                                        { }
                                        {parent ? (
                                            <Link title={parentLabel} to={`/contact/${parent.id}`}>
                                                {parentLabel}
                                            </Link>
                                        ) : (
                                            <span>{parentLabel}</span>
                                        )}
                                    </td>
                                    <td style={{ background: rowBackground }}>
                                        <ContactStatusComponent status={status} />
                                    </td>
                                    <td style={{ background: rowBackground }}>
                                        <SActions className="showOnRowHover">
                                            <MultiActionsButton parentButtonContent={<Add />} iconButton>
                                                <Button onClick={() => openTaskModal(contact)} variant="popinText">
                                                    {t('contact-details.popin-button-task')}
                                                </Button>
                                                <Button onClick={() => openInterviewModal(contact)} variant="popinText">
                                                    {t('contact-details.popin-button-interview')}
                                                </Button>
                                                <Button onClick={() => openApplicationModal(contact)} variant="popinText">
                                                    {t('contact-details.popin-button-application')}
                                                </Button>
                                            </MultiActionsButton>
                                            <button
                                                onClick={() => openEditContactModal(contact)}
                                                title={t('common.edit')}
                                                type="button"
                                            >
                                                <Edit />
                                            </button>
                                            <DeleteContactButton contact={contact} />
                                        </SActions>
                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {paddingBottom > 0 && (
                                            <tr>
                                                <td colSpan={8} style={{ height: `${paddingBottom}px`, padding: 0 }} />
                                            </tr>
                                        )}
                                    </>
                                );
                            })()}
                        </tbody>
                    </STable>
                </SScrollContainer>
            )}
            {contacts.length === 0 ? (
                <SEndRow>
                    <Actions />
                </SEndRow>
            ) : (
                <FloatingButton>
                    <Actions />
                </FloatingButton>
            )}
        </SWrapper>
    );
};

export default ContactList;
